
import type {
  ContextState,
  Dictionary,
  Filter,
  FilterSelectedValues,
  RegularFilter,
} from '../../models'
import { defineComponent } from 'vue'

import { mapActions, mapGetters } from 'vuex'

import { useBrowseListFilter } from '../../composables/use-browse-list-filter'
import { useEndpoints } from '../../composables/use-endpoints'
import { FilterIdEnum, FilterTypeEnum } from '../../enums'

import { CONTEXT_CONSTS } from '../../store/context/context.consts'
import { getStateToolKey } from '../../store/context/utils/get-state-tool-key'

import { EpbButton } from '../shared/epb-button'
import BrowseListFilter from './browse-list-filter.component.vue'
import DateFilterSelector from './date-filter-selector.component.vue'
import DateRangePickerFilterSelector from './date-range-picker-filter-selector.component.vue'
import RealtimeFilterSelector from './realtime-filter-selector.component.vue'
import RegularFilterSelector from './regular-filter-selector.component.vue'

function hasEndpointSelection(values: unknown): boolean {
  return Array.isArray(values) && values.some(Boolean)
}

export default defineComponent({
  name: 'Filters',
  components: {
    BrowseListFilter,
    EpbButton,
  },
  setup() {
    const { endpoints, defaultEndpointId } = useEndpoints()

    return { endpoints, defaultEndpointId }
  },
  data() {
    return {
      objectType: FilterIdEnum.AUDIT_OBJECT_TYPE,
    }
  },
  computed: {
    ...mapGetters(CONTEXT_CONSTS.moduleName, {
      filters: CONTEXT_CONSTS.getters.enabledFilters,
      appliedFiltersCount: CONTEXT_CONSTS.getters.appliedFiltersCount,
    }),
    sortedFilters(): Filter[] {
      return Object.values(this.filters as Dictionary<Filter>).sort(
        (filter1: Filter, filter2: Filter) => {
          if (
            filter1.config.id === FilterIdEnum.DATE ||
            filter1.config.id === FilterIdEnum.REALTIME
          ) {
            return -1
          } else if (filter1.config.id === FilterIdEnum.COMPARE) {
            if (filter2.config.id !== FilterIdEnum.DATE) {
              return -1
            }
          }
          return 0
        },
      )
    },
    clearActionAvailable(): boolean {
      return Object.values(this.filters).some((filter: Filter) => !filter.config.mandatory)
    },
  },
  watch: {
    endpoints(): void {
      this.preselectDefaultEndpoint()
    },
    filters: {
      deep: true,
      handler(): void {
        this.preselectDefaultEndpoint()
      },
    },
  },
  mounted(): void {
    this.preselectDefaultEndpoint()
  },
  props: {
    displayAsBar: Boolean,
  },
  methods: {
    ...mapActions(CONTEXT_CONSTS.moduleName, {
      setFiltersSelectedValues: CONTEXT_CONSTS.actions.setFiltersSelectedValues,
      clearFilters: CONTEXT_CONSTS.actions.clearFilters,
    }),
    getFilterSelectorComponent(filter: Filter) {
      return {
        [FilterTypeEnum.DATE]: () => DateFilterSelector,
        [FilterTypeEnum.DATE_RANGE]: () => DateRangePickerFilterSelector,
        [FilterTypeEnum.REALTIME]: () => RealtimeFilterSelector,
        [FilterTypeEnum.REGULAR]: () => RegularFilterSelector,
      }[filter.config.type]()
    },
    async onFilterChange(filter: Filter, selectedValues: FilterSelectedValues) {
      await this.setFiltersSelectedValues({
        [filter.config.id]: selectedValues,
      })
      this.$emit('contextChanged')
    },
    async onClearClick() {
      await this.clearFilters()
      useBrowseListFilter().clearBrowseFilter()
      this.$emit('contextChanged')
    },
    preselectDefaultEndpoint(): void {
      const endpointFilter = (this.filters as Dictionary<Filter>)[FilterIdEnum.ENDPOINT] as
        | RegularFilter
        | undefined

      if (!endpointFilter || !this.endpoints.length || !this.defaultEndpointId) {
        return
      }

      if (hasEndpointSelection(endpointFilter.selectedValues)) {
        return
      }

      const contextState = this.$store.state[CONTEXT_CONSTS.moduleName] as ContextState
      const persistedEndpointValues =
        contextState[getStateToolKey(contextState)]?.selectedFiltersValues?.[FilterIdEnum.ENDPOINT]

      if (persistedEndpointValues !== undefined) {
        if (
          hasEndpointSelection(persistedEndpointValues) ||
          (Array.isArray(persistedEndpointValues) && persistedEndpointValues.length === 0)
        ) {
          return
        }
      }

      this.setFiltersSelectedValues({
        [FilterIdEnum.ENDPOINT]: [this.defaultEndpointId],
      })
    },
  },
})
